import {
  NONCONFORMITY_SOURCE_LABEL_PT_BR,
  NONCONFORMITY_TYPE_LABEL_PT_BR,
  NonconformitySource,
  NonconformityType,
  createNonconformitySchema,
  type CreateNonconformityInput,
  type GutScoreInput,
} from '@qualicloud/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { EvidenceUploader, type EvidenceReference } from '@/components/sgq/evidence-uploader';
import { GutMatrix } from '@/components/sgq/gut-matrix';
import { NonconformityPicker } from '@/components/sgq/nonconformity-picker';
import {
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Combobox,
  DatePicker,
  Field,
  FormError,
  IconAlertTriangle,
  Input,
  Select,
  Switch,
  Textarea,
  toIso,
  useToast,
} from '@/design-system';
import { nonconformitiesApi, queryKeys, toUserMessage } from '@/lib/api';
import { formLevelMessage, zodIssuesToFieldErrors } from '@/lib/sgq/form-errors';
import { CadastroRapidoDeDepartamento, CadastroRapidoDeColaborador } from '@/components/cadastro-rapido';
import { useDirectory } from '@/lib/use-directory';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ABERTURA DE NÃO-CONFORMIDADE — sgq.md §A.2, §A.6 e §A.7
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O MESMO `createNonconformitySchema` de `@qualicloud/contracts` valida aqui e
 * no DTO do NestJS. Não há um segundo conjunto de regras neste arquivo — e é
 * exatamente essa duplicação que produziu o BUG-NC-10 no legado: o controller
 * normalizava o POST para `titulo`/`descricao`/`data_nc` e o model lia
 * `title_NC`/`evidencia`/`date_val`. Um template que postasse os nomes
 * canônicos gravava a NC com TÍTULO E DESCRIÇÃO VAZIOS, e a validação passava.
 *
 * TRÊS CAMPOS QUE MUDARAM DE NATUREZA
 *  · ORIGEM é campo próprio e obrigatório. No legado o `<select name="origemNC">`
 *    estava COMENTADO no template e a origem era INFERIDA do tipo — um inteiro
 *    1..25 hardcoded em três cópias divergentes (§A.7, BUG-NC-07). Inferência
 *    quebra em silêncio quando o cliente cadastra um tipo novo, e o indicador
 *    "NC por origem" passa a mentir;
 *  · EVIDÊNCIA é o rótulo do campo que no banco se chamava `descricao` e no
 *    formulário `evidencia`. O nome do campo passa a dizer o que ele é;
 *  · DATA DO FATO é `DATE` puro. O legado guardava `VARCHAR dd/mm/aaaa`, o que
 *    exigia `STR_TO_DATE` em toda consulta e impedia comparação de período.
 *
 * As EVIDÊNCIAS sobem ao MEDIA ANTES do comando: no legado a falha de upload
 * não bloqueava a criação e a NC nascia sem a prova do fato (§A.6).
 */

interface FormState {
  readonly title: string;
  readonly evidence: string;
  readonly type: NonconformityType;
  readonly source: NonconformitySource | '';
  readonly departmentId: string;
  readonly assigneeUserId: string;
  readonly detectedOn: string;
  readonly gut: Partial<GutScoreInput>;
}

/**
 * Mensagem em pt-BR dos campos cujo erro do schema sai em vocabulário de
 * implementação: `source` devolve a lista crua dos valores do enum,
 * `departmentId`/`assigneeUserId` devolvem "Identificador inválido." e a matriz
 * GUT em branco falha por TIPO (`nan`) antes de chegar ao mínimo de 1. Nenhuma
 * dessas diz o que falta nem como resolver. A regra continua sendo a do
 * contrato — o que muda aqui é só como o campo é nomeado para quem preenche.
 */
const FIELD_MESSAGE_PT_BR: Readonly<Record<string, string>> = {
  source: 'Escolha a origem do registro.',
  departmentId: 'Escolha o departamento onde o fato ocorreu.',
  assigneeUserId: 'Escolha quem trata a não-conformidade.',
  'gut.severity': 'Escolha um nível de gravidade (1 a 5).',
  'gut.urgency': 'Escolha um nível de urgência (1 a 5).',
  'gut.trend': 'Escolha um nível de tendência (1 a 5).',
};

const withFieldMessages = (
  errors: Readonly<Record<string, string>>,
): Readonly<Record<string, string>> =>
  Object.fromEntries(
    Object.entries(errors).map(([path, message]) => [path, FIELD_MESSAGE_PT_BR[path] ?? message]),
  );

const EMPTY: FormState = {
  title: '',
  evidence: '',
  type: NonconformityType.NONCONFORMITY,
  source: '',
  departmentId: '',
  assigneeUserId: '',
  detectedOn: toIso(new Date()),
  gut: {},
};

export function NonconformityFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const directory = useDirectory();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [evidences, setEvidences] = useState<readonly EvidenceReference[]>([]);
  /**
   * §A.4 — reincidência declarada NA ABERTURA, como no legado
   * (`qualidade.twig:118-132`: um toggle, um box de busca e um hidden com os
   * ids em CSV). Este estado existia como `[]` LITERAL no comando: o campo
   * viajava no contrato, o servidor o gravava e nenhuma tela jamais o
   * preenchia, de modo que a taxa de reincidência dos indicadores era 0% por
   * construção — o mesmo padrão do BUG-TAR-01 do legado.
   */
  const [isRecurrence, setIsRecurrence] = useState(false);
  const [recurrenceOfIds, setRecurrenceOfIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (next: Partial<FormState>) => setForm((current) => ({ ...current, ...next }));

  const submit = useMutation({
    mutationFn: (input: CreateNonconformityInput) => nonconformitiesApi.create(input),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.nonconformities.all() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all() });
      toast({
        tone: 'success',
        title: `Não-conformidade ${created.code} aberta`,
        description:
          'O registro está em aberto e aguarda a avaliação de procedência do gestor. Nada é encerrado nesta etapa.',
      });
      void navigate({
        to: '/nao-conformidades/$nonconformityId',
        params: { nonconformityId: created.id },
      });
    },
    onError: (error) => setSubmitError(toUserMessage(error)),
  });

  /**
   * O comando é montado e VALIDADO PELO SCHEMA DO CONTRATO antes de sair. É a
   * mesma regra que o servidor aplica; o que chega ao `mutate` é o dado já
   * coagido por ele (data em `Date`, números em `number`).
   */
  const handleSubmit = () => {
    setSubmitError(null);

    const candidate = {
      title: form.title,
      evidence: form.evidence,
      type: form.type,
      source: form.source,
      departmentId: form.departmentId,
      assigneeUserId: form.assigneeUserId,
      detectedOn: form.detectedOn,
      gut: form.gut,
      evidenceUploadIds: evidences.map((item) => item.uploadId),
      // Só viaja quando o usuário declarou a reincidência: desligar o toggle
      // depois de escolher não pode deixar o vínculo passar escondido.
      recurrenceOfIds: isRecurrence ? recurrenceOfIds : [],
    };

    const parsed = createNonconformitySchema.safeParse(candidate);
    if (!parsed.success) {
      setFieldErrors(withFieldMessages(zodIssuesToFieldErrors(parsed.error)));
      return;
    }

    setFieldErrors({});
    submit.mutate(parsed.data);
  };

  const formMessage = formLevelMessage(fieldErrors);

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Não-conformidades', to: '/nao-conformidades' }, { label: 'Nova' }]}
        title="Abrir não-conformidade"
        description="Registre o fato, quem trata e a avaliação inicial de criticidade. A procedência é decidida pelo gestor em ato assinado, na etapa seguinte."
      />

      <PageBody>
        <form
          noValidate
          className="flex max-w-5xl flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          {submitError ? (
            <FormError title="Não foi possível abrir a não-conformidade">{submitError}</FormError>
          ) : null}
          {formMessage ? <FormError title="Revise o formulário">{formMessage}</FormError> : null}

          <Card className="flex flex-col gap-4">
            <CardHeader
              headingLevel={2}
              title="Identificação"
              description="O que aconteceu, quando e em qual setor."
            />

            <Field label="Título" required error={fieldErrors.title}>
              <Input
                value={form.title}
                onChange={(event) => patch({ title: event.target.value })}
                maxLength={255}
                autoFocus
                placeholder="Resumo do desvio em uma linha"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Origem"
                required
                error={fieldErrors.source}
                description="De onde veio o registro. É o insumo do indicador “NC por origem”, entrada obrigatória da análise crítica pela direção."
              >
                <Select
                  value={form.source || undefined}
                  onValueChange={(value) => patch({ source: value as NonconformitySource })}
                  placeholder="Selecione a origem…"
                  options={Object.values(NonconformitySource).map((source) => ({
                    value: source,
                    label: NONCONFORMITY_SOURCE_LABEL_PT_BR[source],
                  }))}
                />
              </Field>

              <Field
                label="Natureza"
                required
                error={fieldErrors.type}
                description="Observação e oportunidade de melhoria não exigem plano de ação para serem encerradas; não-conformidade, evento adverso e reclamação exigem."
              >
                <Select
                  value={form.type}
                  onValueChange={(value) => patch({ type: value as NonconformityType })}
                  options={Object.values(NonconformityType).map((type) => ({
                    value: type,
                    label: NONCONFORMITY_TYPE_LABEL_PT_BR[type],
                  }))}
                />
              </Field>
            </div>

            <Field
              label="Evidência"
              required
              error={fieldErrors.evidence}
              description="Descreva o fato observado com os dados que permitem reconstituí-lo: o que, onde, quando, quantidade, lote, equipamento."
            >
              <Textarea
                value={form.evidence}
                onChange={(event) => patch({ evidence: event.target.value })}
                rows={6}
                maxLength={20_000}
                showCounter
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Departamento" required error={fieldErrors.departmentId}>
                <Combobox
                  items={directory.departments.map((department) => ({
                    value: department.id,
                    label: department.name,
                  }))}
                  value={form.departmentId || null}
                  onValueChange={(value) => patch({ departmentId: value ?? '' })}
                  placeholder="Buscar departamento…"
                  emptyMessage="Nenhum departamento disponível."
                  criar={{
                    rotulo: 'Novo departamento',
                    dialogo: (props) => <CadastroRapidoDeDepartamento {...props} />,
                  }}
                />
              </Field>

              <Field
                label="Responsável pelo tratamento"
                required
                error={fieldErrors.assigneeUserId}
              >
                <Combobox
                  items={directory.subjects.map((subject) => ({
                    value: subject.userId,
                    label: subject.displayName,
                    hint: subject.jobTitle,
                  }))}
                  value={form.assigneeUserId || null}
                  onValueChange={(value) => patch({ assigneeUserId: value ?? '' })}
                  placeholder="Buscar colaborador…"
                  criar={{
                    rotulo: 'Novo colaborador',
                    dialogo: (props) => <CadastroRapidoDeColaborador {...props} />,
                  }}
                  emptyMessage="Nenhum colaborador disponível."
                />
              </Field>

              <Field label="Data do fato" required error={fieldErrors.detectedOn}>
                <DatePicker
                  value={form.detectedOn}
                  onValueChange={(iso) => patch({ detectedOn: iso })}
                  max={toIso(new Date())}
                />
              </Field>
            </div>
          </Card>

          {/* ── Matriz GUT ─────────────────────────────────────────────── */}
          <GutMatrix
            value={form.gut}
            onChange={(gut) => patch({ gut })}
            errors={{
              ...(fieldErrors['gut.severity'] ? { severity: fieldErrors['gut.severity'] } : {}),
              ...(fieldErrors['gut.urgency'] ? { urgency: fieldErrors['gut.urgency'] } : {}),
              ...(fieldErrors['gut.trend'] ? { trend: fieldErrors['gut.trend'] } : {}),
            }}
            legend="Matriz GUT — avaliação do emissor"
            description="Sua avaliação inicial de criticidade. O gestor refaz a matriz ao avaliar a procedência, e as duas ficam registradas em separado: a sua não é sobrescrita."
          />

          {/* ── Reincidência (§A.4) ────────────────────────────────────── */}
          <Card className="flex flex-col gap-3">
            <CardHeader
              headingLevel={2}
              title="Reincidência"
              description="Declare se este é o mesmo problema já registrado antes. O vínculo NÃO altera criticidade, não muda situação e não força reunião — é rastreabilidade, e alimenta a taxa de reincidência exigida pela análise de tendência (ISO 13485 §8.4)."
            />
            <Switch
              checked={isRecurrence}
              onCheckedChange={(checked) => {
                setIsRecurrence(checked);
                if (!checked) setRecurrenceOfIds([]);
              }}
              label="Esta NC é reincidência de outra"
              stateLabels={{ on: 'É reincidência', off: 'Registro novo' }}
              disabled={submit.isPending}
            />
            {isRecurrence ? (
              <NonconformityPicker
                selectedIds={recurrenceOfIds}
                onSelectedIdsChange={setRecurrenceOfIds}
                label="Não-conformidades de origem"
                description="Busque por número ou título. Uma NC reincidente pode apontar para várias origens."
                error={fieldErrors.recurrenceOfIds}
                disabled={submit.isPending}
              />
            ) : null}
            {isRecurrence && recurrenceOfIds.length === 0 ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
              >
                <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  Selecione ao menos uma não-conformidade de origem, ou desligue a opção acima.
                </span>
              </p>
            ) : null}
          </Card>

          {/* ── Evidências ─────────────────────────────────────────────── */}
          <Card className="flex flex-col gap-3">
            <CardHeader
              headingLevel={2}
              title="Arquivos de evidência"
              description="Fotos, laudos, relatórios ou registros que comprovem o fato. Cada arquivo é verificado (tamanho, tipo real e antivírus) antes de ser aceito."
            />
            <EvidenceUploader
              value={evidences}
              onChange={setEvidences}
              disabled={submit.isPending}
            />
          </Card>

          <ButtonGroup>
            <Button
              variant="secondary"
              disabled={submit.isPending}
              onClick={() => void navigate({ to: '/nao-conformidades' })}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={submit.isPending}>
              Abrir não-conformidade
            </Button>
          </ButtonGroup>
        </form>
      </PageBody>
    </>
  );
}
